import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { ProposalDto } from "../models/proposal.model";
import { httpService } from "../service/httpService";
import { proposalService } from "../service/proposal.service";
import PageHeader from "./PageHeader";
import "./Proposal.css";

// Extended proposal type that might include client info from the API
interface ProposalWithClientDto extends ProposalDto {
  clientName?: string;
  rmcName?: string;
}

const Proposal: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ rmcClientMappingId?: string }>();
  const [proposals, setProposals] = useState<ProposalWithClientDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem("proposalTableFirst");
    return savedPage ? parseInt(savedPage) : 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const savedRows = localStorage.getItem("proposalTableRows");
    return savedRows ? parseInt(savedRows) : 10;
  });
  const rmcClientMappingIdFromSession =
    sessionStorage.getItem("rmcClientMappingId");
  const rmcClientMappingId =
    location.state?.rmcClientMappingId ||
    params.rmcClientMappingId ||
    rmcClientMappingIdFromSession;
  // derive status options from server response so UI reflects actual statuses
  const statusOptions = useMemo(() => {
    const uniqueStatuses = Array.from(
      new Set(proposals.map((p) => (p as any).status).filter(Boolean))
    ) as string[];
    // Only include actual statuses returned by the API (no "All Status" option)
    const options = uniqueStatuses.map((s) => ({ label: s, value: s }));
    return options;
  }, [proposals]);
  const handleBack = () => {
    if (rmcClientMappingId) {
      // Go back in history (to the exact client page you came from)
      navigate(-1);
    } else {
      // Fallback: go to clients list
      navigate("/clients");
    }
  };
  const loadProposals = useCallback(async () => {
    setLoading(true);
    try {
      // Always load all proposals using the admin endpoint
      const url = `/api/Proposal/List?rmcClientMappingId=${rmcClientMappingId}`;
      const result = await httpService.get<ProposalWithClientDto[]>(
        url,
        undefined,
        {
          unwrap: true,
          normalize: false,
          toast: false,
        }
      );
      sessionStorage.setItem("rmcClientMappingId", rmcClientMappingId);
      const proposals = Array.isArray(result)
        ? result
        : (result as any)?.$values || [];
      setProposals(proposals);
    } catch (error: any) {
      console.error("Error loading proposals:", error);
      if (error.status !== 404) {
        Swal.fire({
          icon: "error",
          title: "Error loading proposals",
          text: error.message || "Failed to load proposals",
        });
      }
      setProposals([]);
    } finally {
      setLoading(false);
    }
  }, [rmcClientMappingId]);

  useEffect(() => {
    if (rmcClientMappingId) {
      loadProposals();
    }
  }, [rmcClientMappingId, loadProposals]);

  const handleCreateProposal = () => {
    const rmcId = location.state?.rmcId || sessionStorage.getItem("rmcId");
    const cityId = location.state?.cityId; // Get cityId from location state

    // Store rmcId in sessionStorage for future use
    if (rmcId) {
      sessionStorage.setItem("rmcId", rmcId);
    }

    // Store cityId in sessionStorage if available
    if (cityId) {
      sessionStorage.setItem("clientCityId", String(cityId));
    }

    navigate("/proposal-wizard", {
      state: {
        rmcClientMappingId: rmcClientMappingId,
        rmcId: rmcId,
        clientId: location.state?.clientId,
        clientName: location.state?.clientName,
        cityId: cityId, // Pass cityId to wizard
      },
    });
  };

  const handleViewProposal = (proposal: ProposalWithClientDto) => {
    const rmcId = location.state?.rmcId || sessionStorage.getItem("rmcId");
    const cityId = location.state?.cityId;

    // Navigate to view mode of the wizard (read-only)
    navigate("/proposal-wizard", {
      state: {
        editProposal: proposal,
        editMode: true,
        viewMode: true, // View-only mode
        rmcClientMappingId: proposal.rmcClientMappingId,
        rmcId: rmcId,
        clientId: location.state?.clientId,
        clientName: location.state?.clientName,
        cityId: cityId,
      },
    });
  };

  const handleEditProposal = (proposal: ProposalWithClientDto) => {
    const rmcId = location.state?.rmcId || sessionStorage.getItem("rmcId");
    const cityId = location.state?.cityId; // Get cityId from location state

    // Navigate to edit mode of the wizard
    navigate("/proposal-wizard", {
      state: {
        editProposal: proposal,
        editMode: true,
        rmcClientMappingId: proposal.rmcClientMappingId, // Ensure mapping ID is passed
        rmcId: rmcId,
        clientId: location.state?.clientId,
        clientName: location.state?.clientName,
        cityId: cityId, // Pass cityId to wizard for edit mode too
      },
    });
  };

  const handleDeleteProposal = async (proposal: ProposalWithClientDto) => {
    const result = await Swal.fire({
      title: "Delete Proposal",
      text: `Are you sure you want to delete "${proposal.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await httpService.delete(`/api/Proposal/${proposal.id}`, undefined, {
          unwrap: true,
          normalize: false,
          toast: false,
        });

        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Proposal has been deleted.",
          timer: 2000,
          showConfirmButton: false,
        });

        await loadProposals(); // Reload the list
      } catch (error: any) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Failed to delete proposal",
        });
      }
    }
  };

  const dateBodyTemplate = (rowData: ProposalWithClientDto, field: string) => {
    const date = rowData[field as keyof ProposalWithClientDto] as string;
    return date ? new Date(date).toLocaleDateString() : "-";
  };

  // Render status coming from API (fallback to date-based computation when missing)
  const statusBodyTemplate = (rowData: ProposalWithClientDto) => {
    const apiStatus = (rowData as any).status as string | undefined;
    let status = apiStatus;

    if (!status) {
      const now = new Date();
      const startDate = new Date(rowData.startDate);
      const endDate = new Date(rowData.endDate);
      if (now < startDate) status = "Pending";
      else if (now >= startDate && now <= endDate) status = "Active";
      else if (now > endDate) status = "Expired";
      else status = "Draft";
    }

    const severityMap: Record<
      string,
      "success" | "info" | "warning" | "danger"
    > = {
      Active: "success",
      Pending: "warning",
      Expired: "danger",
      Draft: "info",
    };

    const severity = severityMap[status] || "info";
    return <Badge value={status} severity={severity} />;
  };

  // Custom filter function for status
  const getProposalStatus = (proposal: ProposalWithClientDto): string => {
    // Prefer API-provided status when available
    const apiStatus = (proposal as any).status as string | undefined;
    if (apiStatus) return apiStatus;

    const now = new Date();
    const startDate = new Date(proposal.startDate);
    const endDate = new Date(proposal.endDate);

    if (now < startDate) return "Pending";
    if (now >= startDate && now <= endDate) return "Active";
    if (now > endDate) return "Expired";
    return "Draft";
  };

  const filteredProposals = proposals.filter((proposal) => {
    const status = (proposal as any).status || getProposalStatus(proposal);
    if (statusFilter && status !== statusFilter) return false;
    if (globalFilter) {
      const gf = globalFilter.toLowerCase();
      const inName = (proposal.name || "").toLowerCase().includes(gf);
      const inClient = ((proposal as any).clientName || "")
        .toLowerCase()
        .includes(gf);
      if (!inName && !inClient) return false;
    }
    return true;
  });

  // const handleToggleActive = async (rowData: ProposalWithClientDto) => {
  //   const newActiveState = !(rowData as any).isActive;

  //   try {
  //     // Optimistically update UI
  //     setProposals((prevProposals) =>
  //       prevProposals.map((p) =>
  //         p.id === rowData.id ? { ...p, isActive: newActiveState } as any : p
  //       )
  //     );

  //     // Call API
  //     await proposalService.toggleProposalActive(rowData.id, newActiveState);
  //   } catch (error: any) {
  //     console.error("Error toggling proposal active state:", error);
  //     // Rollback on error
  //     await loadProposals();
  //     Swal.fire({
  //       icon: "error",
  //       title: "Error",
  //       text: error.message || "Failed to update proposal status",
  //     });
  //   }
  // };

  const handleToggleActive = async (rowData: ProposalWithClientDto) => {
    const newActiveState = !(rowData as any).isActive;

    if (newActiveState) {
      const currentDate = new Date();
      console.log("Current Date:", currentDate.toLocaleDateString());

      const activeApprovedProposal = proposals.find((proposal) => {
        const proposalStatus = (proposal as any).status;
        const isActive = (proposal as any).isActive;
        const startDate = new Date(proposal.startDate);
        const endDate = new Date(proposal.endDate);

        const isApprovedAndActive =
          proposalStatus === "Approved" && isActive === true;
        const isNotCurrentProposal = proposal.id !== rowData.id;

        console.log(`Checking proposal "${proposal.name}":`, {
          status: proposalStatus,
          isActive: isActive,
          startDate: startDate.toLocaleDateString(),
          endDate: endDate.toLocaleDateString(),
          isApprovedAndActive,
          isNotCurrentProposal,
          shouldTriggerWarning: isApprovedAndActive && isNotCurrentProposal,
        });

        return isApprovedAndActive && isNotCurrentProposal;
      });

      if (activeApprovedProposal) {
        const result = await Swal.fire({
          title: "Active Approved Proposal Found",
          text: `There is already an active approved proposal "${
            activeApprovedProposal.name
          }" running from ${new Date(
            activeApprovedProposal.startDate
          ).toLocaleDateString()} to ${new Date(
            activeApprovedProposal.endDate
          ).toLocaleDateString()}. Do you want to continue?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "OK",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
        });

        if (!result.isConfirmed) {
          return;
        }
      }
    }

    try {
      setProposals((prevProposals) =>
        prevProposals.map((p) =>
          p.id === rowData.id ? ({ ...p, isActive: newActiveState } as any) : p
        )
      );

      const response = await proposalService.toggleProposalActive(
        rowData.id,
        newActiveState
      );

      if (
        response?.correlationId === "CONFIRM_REQUIRED" ||
        response?.status === "Warning"
      ) {
        const result = await Swal.fire({
          title: "Confirmation Required",
          text:
            response.message ||
            "Another proposal is already active. Do you want to continue?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, continue",
          cancelButtonText: "No, cancel",
        });

        if (result.isConfirmed) {
          const confirmResponse = await proposalService.toggleProposalActive(
            rowData.id,
            newActiveState
          );

          if (
            confirmResponse?.success &&
            confirmResponse?.status !== "Warning"
          ) {
            // Reload proposals to reflect changes made by backend
            await loadProposals();
            Swal.fire({
              icon: "success",
              title: "Success",
              text:
                confirmResponse.message ||
                "Proposal status updated successfully.",
              timer: 2000,
              showConfirmButton: false,
            });
          } else {
            // Rollback on confirmation failure
            await loadProposals();
            throw new Error(
              confirmResponse?.message ||
                "Failed to activate proposal after confirmation"
            );
          }
        } else {
          // 3️⃣ User canceled — revert toggle back
          await loadProposals();
          return;
        }
      } else if (response?.success && response?.status !== "Warning") {
        // ✅ Normal success (no warning case)
        // Reload proposals to ensure UI is in sync with backend
        await loadProposals();
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.message || "Proposal status updated successfully.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        // Handle case where response doesn't have success flag or other issues
        await loadProposals();
        console.error("Unhandled response:", response);
        throw new Error(
          response?.message ||
            `Unexpected response from server: ${JSON.stringify(response)}`
        );
      }
    } catch (error: any) {
      console.error("Error toggling proposal active state:", error);
      // Rollback on error
      await loadProposals();
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to update proposal status",
      });
    }
  };

  const actionsBodyTemplate = (rowData: ProposalWithClientDto) => {
    // Determine an effective status: prefer API-provided status, otherwise derive from dates
    const rawStatus = (rowData as any).status;
    const effectiveStatus = rawStatus || getProposalStatus(rowData);
    const isApproved = effectiveStatus === "Approved";
    const isProposed = effectiveStatus === "Proposed";
    const isDraft = effectiveStatus === "Draft";

    // Allow editing/deleting ONLY for Draft status
    const canEdit = isDraft;
    const canDelete = isDraft;
    const canToggle = isApproved;

    return (
      <div className="d-flex gap-2 align-items-center">
        <Button
          icon="pi pi-eye"
          rounded
          severity="info"
          tooltip="View Details"
          tooltipOptions={{ position: "top" }}
          onClick={() => handleViewProposal(rowData)}
        />

        {/* Edit Button */}
        {canEdit ? (
          <Button
            icon="pi pi-pencil"
            rounded
            severity="warning"
            tooltip="Edit Proposal"
            tooltipOptions={{ position: "top" }}
            onClick={() => handleEditProposal(rowData)}
          />
        ) : (
          <span
            className="disabled-button-wrapper"
            data-pr-tooltip={`Cannot edit ${String(
              effectiveStatus
            ).toLowerCase()} proposal`}
            data-pr-position="top"
          >
            <Button icon="pi pi-pencil" rounded severity="warning" />
          </span>
        )}

        {canDelete ? (
          <Button
            icon="pi pi-trash"
            rounded
            severity="danger"
            tooltip="Delete Proposal"
            tooltipOptions={{ position: "top" }}
            onClick={() => handleDeleteProposal(rowData)}
          />
        ) : (
          <span
            className="disabled-button-wrapper"
            data-pr-tooltip={`Cannot delete ${String(
              effectiveStatus
            ).toLowerCase()} proposal`}
            data-pr-position="top"
          >
            <Button icon="pi pi-trash" rounded severity="danger" />
          </span>
        )}

        {canToggle && (
          <InputSwitch
            checked={(rowData as any).isActive !== false}
            onChange={() => handleToggleActive(rowData)}
            tooltip={(rowData as any).isActive ? "Deactivate" : "Activate"}
            className="p-inputswitch-sm"
          />
        )}
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          {/* <h5 className="m-0">All Proposals</h5>
          <Badge
            value={filteredProposals.length}
            className="p-badge-secondary"
          />
          {proposals.length !== filteredProposals.length && (
            <small className="text-muted">of {proposals.length} total</small>
          )}
          <Badge value="Admin View" className="p-badge-info" /> */}
        </div>
        <div className="d-flex align-items-center gap-2">
          <Dropdown
            value={statusFilter}
            options={statusOptions}
            onChange={(e) => setStatusFilter(e.value)}
            placeholder="Filter by Status    "
            className="p-dropdown-sm"
            showClear
            appendTo="self"
          />
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                setCurrentPage(0);
                localStorage.setItem("proposalTableFirst", "0");
              }}
              placeholder="     Search"
              className="p-inputtext-sm"
            />
          </IconField>
          <Button
            label="Create Proposal"
            icon="pi pi-plus"
            onClick={handleCreateProposal}
            className="p-button-sm"
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <Tooltip target=".p-button" />
      <div className="content proposal-page">
        <PageHeader
          showBackButton
          onBack={() => {
            const rmcId = location.state?.rmcId;
            if (rmcId) {
              navigate(`/rmc/${rmcId}/clients`);
            }
            // else {
            //   navigate(-1);
            // }
          }}
          title="All Proposals"
        />

        <div className="content-body">
          <div className="container-fluid mt-4">
            <div className="row shadow">
              <div className="col-12">
                <div className="card border-0">
                  <div className="card-body pt-4 pb-4">
                    {/* Stats (compact) */}
                    {proposals.length > 0 && (
                      <div className="row g-3 mb-4 proposal-stats">
                        <div className="col-6 col-md-3">
                          <div
                            className={`stat-tile ${
                              statusFilter === "" ? "active" : ""
                            }`}
                            onClick={() => {
                              setStatusFilter("");
                              setCurrentPage(0);
                              localStorage.setItem("proposalTableFirst", "0");
                            }}
                          >
                            <i className="pi pi-file-edit stat-icon text-primary"></i>
                            <div className="stat-content">
                              <span className="stat-value text-primary">
                                {proposals.length}
                              </span>
                              <span className="stat-label">
                                Total Proposals
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div
                            className={`stat-tile ${
                              statusFilter === "Approved" ? "active" : ""
                            }`}
                            onClick={() => {
                              setStatusFilter("Approved");
                              setCurrentPage(0);
                              localStorage.setItem("proposalTableFirst", "0");
                            }}
                          >
                            <i className="pi pi-check-circle stat-icon text-success"></i>
                            <div className="stat-content">
                              <span className="stat-value text-success">
                                {
                                  proposals.filter(
                                    (p) => getProposalStatus(p) === "Approved"
                                  ).length
                                }
                              </span>
                              <span className="stat-label">
                                Active / Approved
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div
                            className={`stat-tile ${
                              statusFilter === "Proposed" ? "active" : ""
                            }`}
                            onClick={() => {
                              setStatusFilter("Proposed");
                              setCurrentPage(0);
                              localStorage.setItem("proposalTableFirst", "0");
                            }}
                          >
                            <i className="pi pi-clock stat-icon text-warning"></i>
                            <div className="stat-content">
                              <span className="stat-value text-warning">
                                {
                                  proposals.filter(
                                    (p) => getProposalStatus(p) === "Proposed"
                                  ).length
                                }
                              </span>
                              <span className="stat-label">
                                Pending / Proposed
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div
                            className={`stat-tile ${
                              statusFilter === "Expired" ? "active" : ""
                            }`}
                            onClick={() => {
                              setStatusFilter("Expired");
                              setCurrentPage(0);
                              localStorage.setItem("proposalTableFirst", "0");
                            }}
                          >
                            <i className="pi pi-times-circle stat-icon text-danger"></i>
                            <div className="stat-content">
                              <span className="stat-value text-danger">
                                {
                                  proposals.filter(
                                    (p) => getProposalStatus(p) === "Expired"
                                  ).length
                                }
                              </span>
                              <span className="stat-label">Expired</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Data Table */}
                    <DataTable
                      value={filteredProposals}
                      loading={loading}
                      dataKey="id"
                      paginator
                      first={currentPage}
                      rows={rowsPerPage}
                      onPage={(e) => {
                        setCurrentPage(e.first);
                        setRowsPerPage(e.rows);
                        localStorage.setItem(
                          "proposalTableFirst",
                          e.first.toString()
                        );
                        localStorage.setItem(
                          "proposalTableRows",
                          e.rows.toString()
                        );
                      }}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      globalFilter={globalFilter}
                      header={renderHeader()}
                      emptyMessage="No proposals found."
                      className="grid-table"
                      stripedRows
                    >
                      <Column
                        field="name"
                        header="Name"
                        sortable
                        style={{ minWidth: "150px" }}
                      />
                      <>
                        <Column
                          field="clientName"
                          header="Client"
                          sortable
                          style={{ minWidth: "150px" }}
                          body={(rowData) => rowData.clientName || "-"}
                        />
                        <Column
                          field="rmcName"
                          header="RMC"
                          sortable
                          style={{ minWidth: "150px" }}
                          body={(rowData) => rowData.rmcName || "-"}
                        />
                      </>
                      <Column
                        field="referenceCode"
                        header="Reference Code"
                        sortable
                        style={{ minWidth: "200px" }}
                        body={(rowData) => rowData.referenceCode || "-"}
                      />
                      <Column
                        field="description"
                        header="Description"
                        sortable
                        style={{ minWidth: "150px" }}
                        body={(rowData) => (
                          <div
                            className="description-cell"
                            title={rowData.description}
                          >
                            {rowData.description || "-"}
                          </div>
                        )}
                      />
                      <Column
                        field="startDate"
                        header="Start Date"
                        sortable
                        body={(rowData) =>
                          dateBodyTemplate(rowData, "startDate")
                        }
                        style={{ width: "120px" }}
                      />
                      <Column
                        field="endDate"
                        header="End Date"
                        sortable
                        body={(rowData) => dateBodyTemplate(rowData, "endDate")}
                        style={{ width: "120px" }}
                      />
                      <Column
                        field="status"
                        header="Status"
                        sortable
                        body={statusBodyTemplate}
                        style={{ width: "100px" }}
                      />
                      <Column
                        header="Properties"
                        body={(rowData) => {
                          const cityProperties = Array.isArray(
                            rowData.cityProperties
                          )
                            ? rowData.cityProperties
                            : rowData.cityProperties?.$values || [];
                          const total = cityProperties.reduce(
                            (total: number, cp: any) => {
                              const propertyIds = Array.isArray(cp.propertyIds)
                                ? cp.propertyIds
                                : cp.propertyIds?.$values || [];
                              return total + propertyIds.length;
                            },
                            0
                          );
                          return (
                            <Badge value={total} className="p-badge-info" />
                          );
                        }}
                        style={{ width: "100px" }}
                      />
                      <Column
                        header="Actions"
                        body={actionsBodyTemplate}
                        style={{ width: "150px" }}
                        frozen
                        alignFrozen="right"
                      />
                    </DataTable>

                    {filteredProposals.length === 0 && !loading && (
                      <div className="text-center py-5">
                        <div className="mb-4">
                          <i className="pi pi-file empty-state-icon"></i>
                        </div>
                        <h5 className="text-muted mb-3">
                          {proposals.length === 0
                            ? "No Proposals in System"
                            : "No Proposals Match Filter"}
                        </h5>
                        <p className="text-muted mb-4">
                          {proposals.length === 0
                            ? "There are currently no proposals in the system across all clients and RMCs."
                            : "Try adjusting your search criteria or status filter to find proposals."}
                        </p>
                        {proposals.length === 0 && (
                          <Button
                            label="Create New Proposal"
                            icon="pi pi-plus"
                            onClick={handleCreateProposal}
                            className="p-button-lg"
                          />
                        )}
                        {proposals.length > 0 && (
                          <Button
                            label="Clear Filters"
                            icon="pi pi-filter-slash"
                            onClick={() => {
                              setGlobalFilter("");
                              setStatusFilter("");
                            }}
                            className="p-button-outlined"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Proposal;
